import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import axios from 'axios'

import { BASE_URL } from '@/api/api.constants'
import { Branch, Region } from '@/types'
import { RootState } from './store'

export interface RegionState {
  regions: Region[]
  isLoading: boolean
  error: string | null
}

const getInitialState = (): RegionState => {
  try {
    const storedRegions = sessionStorage.getItem('regions')
    if (storedRegions) {
      const parsedRegions = JSON.parse(storedRegions) as Region[]
      if (parsedRegions && parsedRegions.length > 0) {
        return {
          regions: parsedRegions,
          isLoading: false,
          error: null,
        }
      }
    }
  } catch (error) {
    console.warn('Failed to parse stored regions:', error)
  }

  return {
    regions: [],
    isLoading: false,
    error: null,
  }
}

export const refreshRegions = createAsyncThunk<
  Region[],
  void,
  { state: RootState; rejectValue: string }
>('region/refreshRegions', async (_, { getState, rejectWithValue }) => {
  const state = getState()

  if (!state.auth.isAuthenticated) {
    return rejectWithValue('User not authenticated')
  }

  try {
    const response = await axios.get(`${BASE_URL}/regions/`)

    if (!response.data) {
      throw new Error('Failed to fetch regions')
    }

    sessionStorage.setItem('regions', JSON.stringify(response.data))

    return response.data
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to refresh regions'
    return rejectWithValue(errorMessage)
  }
})

const regionSlice = createSlice({
  name: 'region',
  initialState: getInitialState(),
  reducers: {
    setRegions: (state, action: PayloadAction<Region[]>) => {
      state.regions = action.payload
      state.isLoading = false
      state.error = null

      sessionStorage.setItem('regions', JSON.stringify(action.payload))
    },

    clearRegions: (state) => {
      state.regions = []
      state.isLoading = false
      state.error = null
      sessionStorage.removeItem('regions')
    },

    clearRegionError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(refreshRegions.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(refreshRegions.fulfilled, (state, action) => {
        state.regions = action.payload
        state.isLoading = false
        state.error = null
      })
      .addCase(refreshRegions.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Failed to refresh regions'
      })
  },
})

export const { setRegions, clearRegions, clearRegionError } =
  regionSlice.actions

export const regionReducer = regionSlice.reducer

// ============================================================================
// SELECTORS
// ============================================================================

export const selectRegions = (state: RootState) => state.region.regions
export const selectRegionsLoading = (state: RootState) => state.region.isLoading
export const selectRegionsError = (state: RootState) => state.region.error

export const selectAllBranches = createSelector(
  [selectRegions],
  (regions): string[] => {
    return regions.reduce((acc: string[], region: Region) => {
      return [...acc, ...region.branches.map((branch: Branch) => branch.name)]
    }, [])
  }
)
